/**
 * MuiAnalyticsDashboard - Optimized Analytics Dashboard
 *
 * OPTIMIZATION: Phase 11.6 - Dashboard optimization
 *
 * Changes made:
 * - Replaced multiple separate API calls with fetchAnalyticsDashboardData()
 * - fetchAnalyticsDashboardData fetches orders, invoices, payments in parallel
 * - Only required fields are fetched (no products table needed - product data is in orders.products)
 * - Removed unused getProducts() call
 *
 * Benefits:
 * - Reduced data transfer by fetching only needed fields
 * - Parallel fetching maintained for optimal performance
 * - Cleaner code with centralized data fetching
 */
import React, { useState, useEffect, useMemo, useCallback } from "react"
import { useNavigate, Link as RouterLink } from "react-router-dom"
import { useTranslation } from "react-i18next"
import moment from "moment"

// MUI Components
import {
  Box,
  Paper,
  Grid,
  Typography,
  Stack,
  Avatar,
  Chip,
  IconButton,
  Collapse,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  Button,
  Tooltip,
  TextField,
} from "@mui/material"

// MUI Icons
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import NavigateNextIcon from "@mui/icons-material/NavigateNext"
import RefreshIcon from "@mui/icons-material/Refresh"
import TrendingUpIcon from "@mui/icons-material/TrendingUp"
import TrendingDownIcon from "@mui/icons-material/TrendingDown"
import ReceiptIcon from "@mui/icons-material/Receipt"
import InventoryIcon from "@mui/icons-material/Inventory"
import LocalShippingIcon from "@mui/icons-material/LocalShipping"
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import WarningIcon from "@mui/icons-material/Warning"
import CheckCircleIcon from "@mui/icons-material/CheckCircle"
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart"
import PaymentIcon from "@mui/icons-material/Payment"
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn"
import TodayIcon from "@mui/icons-material/Today"
import PrintIcon from "@mui/icons-material/Print"
import SearchIcon from "@mui/icons-material/Search"
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth"

// Recharts
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts"

// API Helpers
import { getOrders, getInvoices, getPayments, getLoans } from "../../helpers/api_helper"

const COLORS = [
  "#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6",
  "#06b6d4", "#ec4899", "#84cc16", "#f97316", "#6366f1",
]

const formatCurrency = (value) => {
  return new Intl.NumberFormat("el-GR", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value || 0)
}

// Collapsible Section Component
const CollapsibleSection = ({ title, icon, expanded, onToggle, badge, badgeColor, children }) => (
  <>
    <Box
      onClick={onToggle}
      sx={{
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        cursor: "pointer",
        bgcolor: expanded ? "#f8fafc" : "transparent",
        borderBottom: expanded ? "1px solid #e2e8f0" : "none",
        "&:hover": { bgcolor: "#f8fafc" },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 32, height: 32 }}>
          {icon}
        </Avatar>
        <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
          {title}
        </Typography>
        {badge && (
          <Chip
            label={badge}
            size="small"
            sx={{ height: 20, fontSize: "0.7rem", bgcolor: badgeColor || "#ef4444", color: "#fff" }}
          />
        )}
      </Stack>
      <IconButton size="small">
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Box>
    <Collapse in={expanded}>
      <Box sx={{ p: 2 }}>{children}</Box>
    </Collapse>
  </>
)

// KPI Stat Card Component
const KpiCard = ({ icon, value, label, color, trend }) => (
  <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", flex: 1, minWidth: 180 }}>
    <Stack direction="row" alignItems="center" spacing={1.5}>
      <Avatar sx={{ bgcolor: `${color}20`, color: color, width: 40, height: 40 }}>
        {icon}
      </Avatar>
      <Box>
        <Typography variant="h6" fontWeight={700} color="#1e293b">
          {value}
        </Typography>
        <Typography variant="caption" color="#64748b">
          {label}
        </Typography>
      </Box>
      {trend !== undefined && (
        <Chip
          icon={trend >= 0 ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
          label={`${trend >= 0 ? "+" : ""}${trend.toFixed(1)}%`}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            bgcolor: trend >= 0 ? "#dcfce7" : "#fef2f2",
            color: trend >= 0 ? "#16a34a" : "#dc2626",
            "& .MuiChip-icon": { color: trend >= 0 ? "#16a34a" : "#dc2626" },
          }}
        />
      )}
    </Stack>
  </Paper>
)

const MuiAnalyticsDashboard = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Data states
  const [orders, setOrders] = useState([])
  const [invoices, setInvoices] = useState([])
  const [allPayments, setAllPayments] = useState([])
  const [loans, setLoans] = useState([])
  const [loading, setLoading] = useState(true)

  // Filter states - default to "All Time" for full data visibility
  const [dateRange, setDateRange] = useState("allTime")
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)

  // UI states
  const [expandedPanels, setExpandedPanels] = useState({
    sales: true,
    expenses: true,
    production: true,
    delivery: true,
  })
  const [salesTab, setSalesTab] = useState(0)
  const [expensesTab, setExpensesTab] = useState(0)
  const [productionTab, setProductionTab] = useState(0)

  // Summary date filter states
  const [summaryFromDate, setSummaryFromDate] = useState(moment().format("YYYY-MM-DD"))
  const [summaryToDate, setSummaryToDate] = useState(moment().format("YYYY-MM-DD"))
  const [appliedSummaryFrom, setAppliedSummaryFrom] = useState(moment().startOf("day"))
  const [appliedSummaryTo, setAppliedSummaryTo] = useState(moment().endOf("day"))

  // Apply summary date filter
  const handleApplySummaryFilter = useCallback(() => {
    setAppliedSummaryFrom(moment(summaryFromDate).startOf("day"))
    setAppliedSummaryTo(moment(summaryToDate).endOf("day"))
  }, [summaryFromDate, summaryToDate])

  document.title = t("Analytics Dashboard") + " | FlexFlow ERP"

  const datePresets = useMemo(() => [
    { key: "allTime", label: t("All Time"), from: null, to: null },
    { key: "thisYear", label: t("This Year"), from: moment().startOf("year"), to: moment() },
    { key: "last3Months", label: t("Last 3 Months"), from: moment().subtract(3, "months").startOf("month"), to: moment() },
    { key: "thisMonth", label: t("This Month"), from: moment().startOf("month"), to: moment() },
  ], [t])

  // ============================================================================
  // OPTIMIZED DATA FETCHING
  // Uses fetchAnalyticsDashboardData() which fetches only required fields
  // ============================================================================
  const fetchAllData = useCallback(async () => {
    setLoading(true)
    try {
      const [ordersRes, invoicesRes, paymentsRes, loansRes] = await Promise.all([
        getOrders(), // Get all orders directly
        getInvoices(),
        getPayments(),
        getLoans(),
      ])

      setOrders(ordersRes || [])
      setInvoices(invoicesRes || [])
      setAllPayments(paymentsRes || [])
      setLoans(loansRes || [])
    } catch (error) {
      console.error("Error fetching analytics data:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  const handleDateRangeChange = (event, newValue) => {
    if (newValue) {
      setDateRange(newValue)
      const found = datePresets.find((p) => p.key === newValue)
      if (found && found.from) {
        setFromDate(found.from)
        setToDate(found.to)
      }
    }
  }

  const togglePanel = (panel) => {
    setExpandedPanels((prev) => ({ ...prev, [panel]: !prev[panel] }))
  }

  // Filter data by date range
  const filteredData = useMemo(() => {
    // If "All Time" selected (null dates), return all data
    if (!fromDate || !toDate) {
      return {
        orders: orders,
        invoices: invoices,
        payments: allPayments,
      }
    }

    const from = fromDate.clone().startOf("day")
    const to = toDate.clone().endOf("day")

    return {
      orders: orders.filter((o) => {
        const date = moment(o.created || o.expected_delivery_date)
        return date.isValid() && date.isBetween(from, to, null, "[]")
      }),
      invoices: invoices.filter((i) => {
        const date = moment(i.importDate)
        return date.isValid() && date.isBetween(from, to, null, "[]")
      }),
      payments: allPayments.filter((p) => {
        const date = moment(p.date)
        return date.isValid() && date.isBetween(from, to, null, "[]")
      }),
    }
  }, [orders, invoices, allPayments, fromDate, toDate])

  // KPI Stats
  const kpiStats = useMemo(() => {
    // Total Sales (from SHIPPED or INVOICED orders)
    const completedOrders = filteredData.orders.filter((o) =>
      o.status === "SHIPPED" || o.status === "INVOICED"
    )
    const totalSales = completedOrders.reduce((sum, o) => {
      try {
        const totals = JSON.parse(o.totals || "{}")
        return sum + (Number(totals.totalOrder) || 0)
      } catch {
        return sum
      }
    }, 0)

    // Total Expenses (from invoices)
    const totalExpenses = filteredData.invoices.reduce((sum, i) => {
      try {
        const totals = JSON.parse(i.totals || "{}")
        return sum + (Number(totals.totalOrder) || 0)
      } catch {
        return sum
      }
    }, 0)

    // Net Profit
    const netProfit = totalSales - totalExpenses

    // Delivery Rate
    const ordersWithDelivery = filteredData.orders.filter(
      (o) => o.expected_delivery_date && (o.status === "SHIPPED" || o.status === "INVOICED")
    )
    const onTimeDeliveries = ordersWithDelivery.filter((o) => {
      const expected = moment(o.expected_delivery_date)
      const completed = moment(o.updated || o.created)
      return completed.isSameOrBefore(expected, "day")
    })
    const deliveryRate =
      ordersWithDelivery.length > 0
        ? (onTimeDeliveries.length / ordersWithDelivery.length) * 100
        : 100

    return { totalSales, totalExpenses, netProfit, deliveryRate, orderCount: completedOrders.length }
  }, [filteredData])

  // Summary Analytics (filtered by date range)
  const todaySummary = useMemo(() => {
    const rangeStart = appliedSummaryFrom
    const rangeEnd = appliedSummaryTo

    // Filtered Orders
    const filteredOrders = orders.filter((o) => {
      const date = moment(o.created)
      return date.isValid() && date.isBetween(rangeStart, rangeEnd, null, "[]")
    })
    const filteredOrdersValue = filteredOrders.reduce((sum, o) => {
      try {
        const totals = JSON.parse(o.totals || "{}")
        return sum + (Number(totals.totalOrder) || 0)
      } catch {
        return sum
      }
    }, 0)

    // Filtered Payments
    const filteredPayments = allPayments.filter((p) => {
      const date = moment(p.date)
      return date.isValid() && date.isBetween(rangeStart, rangeEnd, null, "[]")
    })
    const filteredPaymentsValue = filteredPayments.reduce((sum, p) => {
      return sum + (Number(p.amount) || 0)
    }, 0)

    // Group payments by type
    const paymentsByType = {}
    filteredPayments.forEach((p) => {
      const type = p.type || t("Other")
      paymentsByType[type] = (paymentsByType[type] || 0) + (Number(p.amount) || 0)
    })

    // Invoiced Loans in date range
    const filteredInvoicedLoans = loans.filter((loan) => {
      const invoicedDate = moment(loan.invoiced_date || loan.updated || loan.created)
      const inRange = invoicedDate.isValid() && invoicedDate.isBetween(rangeStart, rangeEnd, null, "[]")
      const isInvoiced = loan.status === "INVOICED" || loan.status === "RETURNED" || loan.invoiced
      return inRange && isInvoiced
    })

    // Orders by status
    const ordersByStatus = {}
    filteredOrders.forEach((o) => {
      const status = o.status || "UNKNOWN"
      ordersByStatus[status] = (ordersByStatus[status] || 0) + 1
    })

    // Payment inbound vs outbound
    const inboundPayments = filteredPayments.filter((p) => p.direction === "INBOUND" || p.type === "ΕΙΣΠΡΑΞΗ")
    const outboundPayments = filteredPayments.filter((p) => p.direction === "OUTBOUND" || p.type === "ΠΛΗΡΩΜΗ")
    const inboundTotal = inboundPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0)
    const outboundTotal = outboundPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0)

    return {
      orders: {
        count: filteredOrders.length,
        value: filteredOrdersValue,
        byStatus: ordersByStatus,
        list: filteredOrders,
      },
      payments: {
        count: filteredPayments.length,
        value: filteredPaymentsValue,
        byType: paymentsByType,
        inbound: { count: inboundPayments.length, value: inboundTotal },
        outbound: { count: outboundPayments.length, value: outboundTotal },
        list: filteredPayments,
      },
      loans: {
        invoicedCount: filteredInvoicedLoans.length,
        list: filteredInvoicedLoans,
      },
    }
  }, [orders, allPayments, loans, t, appliedSummaryFrom, appliedSummaryTo])

  // Print Today's Summary
  const handlePrintTodaySummary = useCallback(() => {
    const printWindow = window.open("", "_blank")
    if (!printWindow) return

    const orderStatusColors = {
      SHIPPED: { bg: "#dcfce7", color: "#16a34a" },
      INVOICED: { bg: "#dbeafe", color: "#2563eb" },
      PENDING: { bg: "#fef3c7", color: "#d97706" },
      PRODUCTION: { bg: "#f3e8ff", color: "#7c3aed" },
    }

    const orderStatusHtml = Object.entries(todaySummary.orders.byStatus)
      .map(([status, count]) => {
        const colors = orderStatusColors[status] || { bg: "#f1f5f9", color: "#475569" }
        return `<span style="display: inline-block; padding: 4px 12px; margin: 2px; border-radius: 12px; font-size: 12px; font-weight: 500; background: ${colors.bg}; color: ${colors.color};">${status}: ${count}</span>`
      })
      .join("")

    const loansListHtml = todaySummary.loans.list
      .slice(0, 5)
      .map((loan) => `<li style="margin: 4px 0; font-size: 13px;">${loan.product_name || loan.name || t("Loan")} - ${loan.customer_name || ""}</li>`)
      .join("")

    // Format date range for display
    const isSameDay = appliedSummaryFrom.isSame(appliedSummaryTo, "day")
    const dateRangeText = isSameDay
      ? appliedSummaryFrom.format("dddd, D MMMM YYYY")
      : `${appliedSummaryFrom.format("D MMM YYYY")} - ${appliedSummaryTo.format("D MMM YYYY")}`

    const html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${t("Summary")} - ${dateRangeText}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            padding: 40px;
            background: #fff;
            color: #1e293b;
          }
          .header {
            text-align: center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 3px solid #3b82f6;
          }
          .header h1 {
            font-size: 28px;
            color: #0c4a6e;
            margin-bottom: 8px;
          }
          .header .date {
            font-size: 16px;
            color: #64748b;
          }
          .header .company {
            font-size: 14px;
            color: #94a3b8;
            margin-top: 4px;
          }
          .kpi-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
            margin-bottom: 40px;
          }
          .kpi-card {
            border: 2px solid #e2e8f0;
            border-radius: 16px;
            padding: 24px;
            text-align: center;
          }
          .kpi-card.orders { border-color: #3b82f6; }
          .kpi-card.payments { border-color: #16a34a; }
          .kpi-card.loans { border-color: #d97706; }
          .kpi-card .icon {
            font-size: 32px;
            margin-bottom: 12px;
          }
          .kpi-card .title {
            font-size: 14px;
            text-transform: uppercase;
            letter-spacing: 1px;
            color: #64748b;
            margin-bottom: 12px;
            font-weight: 600;
          }
          .kpi-card .main-value {
            font-size: 48px;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .kpi-card.orders .main-value { color: #3b82f6; }
          .kpi-card.payments .main-value { color: #16a34a; }
          .kpi-card.loans .main-value { color: #d97706; }
          .kpi-card .sub-value {
            font-size: 20px;
            font-weight: 600;
            color: #1e293b;
            margin-bottom: 16px;
          }
          .kpi-card .details {
            font-size: 13px;
            color: #64748b;
            border-top: 1px solid #e2e8f0;
            padding-top: 16px;
            margin-top: 8px;
          }
          .status-chips {
            margin-top: 12px;
          }
          .payment-breakdown {
            display: flex;
            justify-content: center;
            gap: 24px;
            margin-top: 12px;
          }
          .payment-item {
            text-align: center;
          }
          .payment-item .label {
            font-size: 11px;
            color: #64748b;
            text-transform: uppercase;
          }
          .payment-item .value {
            font-size: 16px;
            font-weight: 600;
          }
          .payment-item.inbound .value { color: #16a34a; }
          .payment-item.outbound .value { color: #ef4444; }
          .loans-list {
            text-align: left;
            margin-top: 12px;
            padding-left: 20px;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #e2e8f0;
            color: #94a3b8;
            font-size: 12px;
          }
          @media print {
            body { padding: 20px; }
            .kpi-card { break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>📊 ${t("Summary Report")}</h1>
          <div class="date">${dateRangeText}</div>
          <div class="company">FlexFlow ERP</div>
        </div>

        <div class="kpi-grid">
          <div class="kpi-card orders">
            <div class="icon">🛒</div>
            <div class="title">${t("Orders")}</div>
            <div class="main-value">${todaySummary.orders.count}</div>
            <div class="sub-value">${formatCurrency(todaySummary.orders.value)}</div>
            ${Object.keys(todaySummary.orders.byStatus).length > 0 ? `
              <div class="details">
                <div style="margin-bottom: 8px; font-weight: 500;">${t("By Status")}:</div>
                <div class="status-chips">${orderStatusHtml}</div>
              </div>
            ` : ""}
          </div>

          <div class="kpi-card payments">
            <div class="icon">💳</div>
            <div class="title">${t("Payments")}</div>
            <div class="main-value">${todaySummary.payments.count}</div>
            <div class="sub-value">${formatCurrency(todaySummary.payments.value)}</div>
            <div class="details">
              <div class="payment-breakdown">
                <div class="payment-item inbound">
                  <div class="value">▲ ${formatCurrency(todaySummary.payments.inbound.value)}</div>
                  <div class="label">${t("Inbound")} (${todaySummary.payments.inbound.count})</div>
                </div>
                <div class="payment-item outbound">
                  <div class="value">▼ ${formatCurrency(todaySummary.payments.outbound.value)}</div>
                  <div class="label">${t("Outbound")} (${todaySummary.payments.outbound.count})</div>
                </div>
              </div>
            </div>
          </div>

          <div class="kpi-card loans">
            <div class="icon">📋</div>
            <div class="title">${t("Loans Invoiced")}</div>
            <div class="main-value">${todaySummary.loans.invoicedCount}</div>
            <div class="sub-value">${t("Loans invoiced today")}</div>
            ${todaySummary.loans.invoicedCount > 0 ? `
              <div class="details">
                <div style="margin-bottom: 8px; font-weight: 500;">${t("Recent")}:</div>
                <ul class="loans-list">${loansListHtml}</ul>
                ${todaySummary.loans.list.length > 5 ? `<div style="margin-top: 8px; font-size: 12px;">+${todaySummary.loans.list.length - 5} ${t("more")}</div>` : ""}
              </div>
            ` : `<div class="details" style="font-style: italic;">${t("No loans invoiced today")}</div>`}
          </div>
        </div>

        <div class="footer">
          ${t("Generated on")} ${moment().format("DD/MM/YYYY HH:mm")} • FlexFlow ERP
        </div>

        <script>
          window.onload = function() { window.print(); }
        </script>
      </body>
      </html>
    `

    printWindow.document.write(html)
    printWindow.document.close()
  }, [todaySummary, t, appliedSummaryFrom, appliedSummaryTo])

  // Sales Analytics
  const salesAnalytics = useMemo(() => {
    // Revenue by month
    const revenueByMonth = {}
    filteredData.orders.forEach((order) => {
      if (order.status === "SHIPPED" || order.status === "INVOICED") {
        const month = moment(order.updated || order.created).format("MMM YY")
        try {
          const totals = JSON.parse(order.totals || "{}")
          revenueByMonth[month] = (revenueByMonth[month] || 0) + (Number(totals.totalOrder) || 0)
        } catch {}
      }
    })

    // Top customers
    const customerTotals = {}
    filteredData.orders.forEach((order) => {
      if (order.status === "SHIPPED" || order.status === "INVOICED") {
        const name = order.name || "Unknown"
        try {
          const totals = JSON.parse(order.totals || "{}")
          customerTotals[name] = (customerTotals[name] || 0) + (Number(totals.totalOrder) || 0)
        } catch {}
      }
    })
    const topCustomers = Object.entries(customerTotals)
      .map(([name, total]) => ({ name: name.substring(0, 15), total }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 10)

    // Orders over time (for line chart)
    const ordersOverTime = Object.entries(revenueByMonth)
      .map(([month, value]) => ({ month, value }))
      .sort((a, b) => moment(a.month, "MMM YY").diff(moment(b.month, "MMM YY")))

    return { revenueByMonth: ordersOverTime, topCustomers }
  }, [filteredData])

  // Expenses Analytics
  const expensesAnalytics = useMemo(() => {
    // By expense code (refcode)
    const byCode = {}
    filteredData.invoices.forEach((inv) => {
      const code = inv.refcode || inv.Refcode || t("Uncategorized")
      try {
        const totals = JSON.parse(inv.totals || "{}")
        byCode[code] = (byCode[code] || 0) + (Number(totals.totalOrder) || 0)
      } catch {}
    })

    const expensesByCategory = Object.entries(byCode)
      .map(([code, total], index) => ({
        code: code.substring(0, 12),
        total,
        fill: COLORS[index % COLORS.length],
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 8)

    // Monthly trend
    const monthlyTrend = {}
    filteredData.invoices.forEach((inv) => {
      const month = moment(inv.importDate).format("MMM YY")
      try {
        const totals = JSON.parse(inv.totals || "{}")
        monthlyTrend[month] = (monthlyTrend[month] || 0) + (Number(totals.totalOrder) || 0)
      } catch {}
    })

    return {
      byCategory: expensesByCategory,
      monthlyTrend: Object.entries(monthlyTrend)
        .map(([month, value]) => ({ month, value }))
        .sort((a, b) => moment(a.month, "MMM YY").diff(moment(b.month, "MMM YY"))),
    }
  }, [filteredData, t])

  // Production Analytics
  const productionAnalytics = useMemo(() => {
    // Products produced (from shipped orders)
    const productsSold = {}
    filteredData.orders.forEach((order) => {
      if (order.status === "SHIPPED" || order.status === "INVOICED") {
        try {
          const prods = JSON.parse(order.products || "[]")
          prods.forEach((p) => {
            const name = p.name || "Unknown"
            productsSold[name] = (productsSold[name] || 0) + (Number(p.quantity) || 0)
          })
        } catch {}
      }
    })

    const topProducts = Object.entries(productsSold)
      .map(([name, quantity]) => ({ name: name.substring(0, 15), quantity }))
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10)

    // Total units sold
    const totalUnits = topProducts.reduce((sum, p) => sum + p.quantity, 0)

    return { topProducts, totalUnits }
  }, [filteredData])

  // Delivery Analytics
  const deliveryAnalytics = useMemo(() => {
    const today = moment().startOf("day")
    const activeOrders = filteredData.orders.filter(
      (o) => !["SHIPPED", "RETURNED", "OFFER", "CANCELED", "OFFER-DELETE"].includes(o.status)
    )

    // Categorize by delivery urgency
    const urgencyCategories = {
      overdue: { count: 0, label: t("Overdue"), color: "#ef4444" },
      today: { count: 0, label: t("Today"), color: "#f59e0b" },
      tomorrow: { count: 0, label: t("Tomorrow"), color: "#f97316" },
      thisWeek: { count: 0, label: t("This Week"), color: "#3b82f6" },
      nextWeek: { count: 0, label: t("Next Week"), color: "#8b5cf6" },
      later: { count: 0, label: t("Later"), color: "#6b7280" },
    }

    activeOrders.forEach((order) => {
      if (!order.expected_delivery_date) return
      const deliveryDate = moment(order.expected_delivery_date).startOf("day")
      const daysUntil = deliveryDate.diff(today, "days")

      if (daysUntil < 0) urgencyCategories.overdue.count++
      else if (daysUntil === 0) urgencyCategories.today.count++
      else if (daysUntil === 1) urgencyCategories.tomorrow.count++
      else if (daysUntil <= 7) urgencyCategories.thisWeek.count++
      else if (daysUntil <= 14) urgencyCategories.nextWeek.count++
      else urgencyCategories.later.count++
    })

    const urgencyData = Object.values(urgencyCategories).map((cat) => ({
      name: cat.label,
      count: cat.count,
      fill: cat.color,
    }))

    // Average delivery time (for shipped orders)
    const shippedWithDates = filteredData.orders.filter(
      (o) => o.status === "SHIPPED" && o.expected_delivery_date && o.updated
    )
    const avgDeliveryTime =
      shippedWithDates.length > 0
        ? Math.abs(
            shippedWithDates.reduce((sum, o) => {
              const created = moment(o.created)
              const actual = moment(o.updated)
              return sum + actual.diff(created, "days")
            }, 0) / shippedWithDates.length
          )
        : 0

    return {
      urgencyData,
      avgDeliveryTime,
      overdueCount: urgencyCategories.overdue.count,
      activeOrdersCount: activeOrders.length,
    }
  }, [filteredData, t])

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#f8fafc",
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ px: 1.5, py: 1.5, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: "#64748b" }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" sx={{ color: "#94a3b8" }} />}>
              <Link
                component={RouterLink}
                to="/dashboard"
                sx={{ color: "#64748b", textDecoration: "none", fontSize: "0.875rem" }}
              >
                {t("Dashboards")}
              </Link>
              <Typography color="#1e293b" fontSize="0.875rem" fontWeight={500}>
                {t("Analytics Dashboard")}
              </Typography>
            </Breadcrumbs>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <ToggleButtonGroup
              value={dateRange}
              exclusive
              onChange={handleDateRangeChange}
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  px: 1.5,
                  py: 0.5,
                  fontSize: "0.75rem",
                  borderColor: "#e2e8f0",
                  "&.Mui-selected": {
                    bgcolor: "#3b82f6",
                    color: "#fff",
                    "&:hover": { bgcolor: "#2563eb" },
                  },
                },
              }}
            >
              {datePresets.map((p) => (
                <ToggleButton key={p.key} value={p.key}>
                  {p.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <IconButton size="small" onClick={fetchAllData} sx={{ color: "#64748b" }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      </Paper>

      {/* KPI Stats Row */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <KpiCard
          icon={<TrendingUpIcon sx={{ fontSize: 20 }} />}
          value={formatCurrency(kpiStats.totalSales)}
          label={t("Total Sales")}
          color="#3b82f6"
        />
        <KpiCard
          icon={<ReceiptIcon sx={{ fontSize: 20 }} />}
          value={formatCurrency(kpiStats.totalExpenses)}
          label={t("Total Expenses")}
          color="#ef4444"
        />
        <KpiCard
          icon={<AccountBalanceWalletIcon sx={{ fontSize: 20 }} />}
          value={formatCurrency(kpiStats.netProfit)}
          label={t("Net Profit")}
          color={kpiStats.netProfit >= 0 ? "#10b981" : "#ef4444"}
        />
        <KpiCard
          icon={
            kpiStats.deliveryRate >= 90 ? (
              <CheckCircleIcon sx={{ fontSize: 20 }} />
            ) : (
              <WarningIcon sx={{ fontSize: 20 }} />
            )
          }
          value={`${kpiStats.deliveryRate.toFixed(1)}%`}
          label={t("On-Time Delivery")}
          color={kpiStats.deliveryRate >= 90 ? "#10b981" : kpiStats.deliveryRate >= 70 ? "#f59e0b" : "#ef4444"}
        />
      </Stack>

      {/* Summary Section with Date Filter */}
      <Paper elevation={0} sx={{ mb: 1.5, borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: "#f0f9ff",
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            {/* Title */}
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Avatar sx={{ bgcolor: "#0ea5e9", color: "#fff", width: 36, height: 36 }}>
                <CalendarMonthIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} color="#0c4a6e">
                  {t("Summary")}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  {appliedSummaryFrom.isSame(appliedSummaryTo, "day")
                    ? appliedSummaryFrom.format("dddd, D MMMM YYYY")
                    : `${appliedSummaryFrom.format("D MMM YYYY")} - ${appliedSummaryTo.format("D MMM YYYY")}`}
                </Typography>
              </Box>
            </Stack>

            {/* Date Filters and Actions */}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  type="date"
                  size="small"
                  label={t("From")}
                  value={summaryFromDate}
                  onChange={(e) => setSummaryFromDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: 150,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#fff",
                      "& fieldset": { borderColor: "#e2e8f0" },
                    },
                  }}
                />
                <TextField
                  type="date"
                  size="small"
                  label={t("To")}
                  value={summaryToDate}
                  onChange={(e) => setSummaryToDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    width: 150,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#fff",
                      "& fieldset": { borderColor: "#e2e8f0" },
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SearchIcon />}
                  onClick={handleApplySummaryFilter}
                  sx={{
                    bgcolor: "#3b82f6",
                    "&:hover": { bgcolor: "#2563eb" },
                    textTransform: "none",
                    fontWeight: 500,
                    px: 2,
                    minWidth: 100,
                  }}
                >
                  {t("Apply")}
                </Button>
              </Stack>
              <Tooltip title={t("Print Summary")}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintTodaySummary}
                  sx={{
                    borderColor: "#0ea5e9",
                    color: "#0ea5e9",
                    "&:hover": { borderColor: "#0284c7", bgcolor: "#f0f9ff" },
                    textTransform: "none",
                    fontWeight: 500,
                    px: 2,
                  }}
                >
                  {t("Print")}
                </Button>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>

        <Grid container spacing={0}>
          {/* Today's Orders */}
          <Grid item xs={12} md={4} sx={{ borderRight: { md: "1px solid #e2e8f0" }, borderBottom: { xs: "1px solid #e2e8f0", md: "none" } }}>
            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6", width: 32, height: 32 }}>
                  <ShoppingCartIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                  {t("Orders")}
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="h4" fontWeight={700} color="#3b82f6">
                    {todaySummary.orders.count}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Total Orders")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h5" fontWeight={700} color="#1e293b">
                    {formatCurrency(todaySummary.orders.value)}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Order Value")}
                  </Typography>
                </Grid>
              </Grid>

              {Object.keys(todaySummary.orders.byStatus).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="#64748b" sx={{ mb: 1, display: "block" }}>
                    {t("By Status")}:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {Object.entries(todaySummary.orders.byStatus).map(([status, count]) => (
                      <Chip
                        key={status}
                        label={`${status}: ${count}`}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.7rem",
                          bgcolor:
                            status === "SHIPPED" ? "#dcfce7" :
                            status === "INVOICED" ? "#dbeafe" :
                            status === "PENDING" ? "#fef3c7" :
                            status === "PRODUCTION" ? "#f3e8ff" :
                            "#f1f5f9",
                          color:
                            status === "SHIPPED" ? "#16a34a" :
                            status === "INVOICED" ? "#2563eb" :
                            status === "PENDING" ? "#d97706" :
                            status === "PRODUCTION" ? "#7c3aed" :
                            "#475569",
                        }}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Today's Payments */}
          <Grid item xs={12} md={4} sx={{ borderRight: { md: "1px solid #e2e8f0" }, borderBottom: { xs: "1px solid #e2e8f0", md: "none" } }}>
            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#dcfce7", color: "#16a34a", width: 32, height: 32 }}>
                  <PaymentIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                  {t("Payments")}
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="h4" fontWeight={700} color="#16a34a">
                    {todaySummary.payments.count}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Total Payments")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="h5" fontWeight={700} color="#1e293b">
                    {formatCurrency(todaySummary.payments.value)}
                  </Typography>
                  <Typography variant="caption" color="#64748b">
                    {t("Total Amount")}
                  </Typography>
                </Grid>
              </Grid>

              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={2}>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <TrendingUpIcon sx={{ fontSize: 16, color: "#16a34a" }} />
                      <Typography variant="body2" fontWeight={600} color="#16a34a">
                        {formatCurrency(todaySummary.payments.inbound.value)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="#64748b">
                      {t("Inbound")} ({todaySummary.payments.inbound.count})
                    </Typography>
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <TrendingDownIcon sx={{ fontSize: 16, color: "#ef4444" }} />
                      <Typography variant="body2" fontWeight={600} color="#ef4444">
                        {formatCurrency(todaySummary.payments.outbound.value)}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="#64748b">
                      {t("Outbound")} ({todaySummary.payments.outbound.count})
                    </Typography>
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Grid>

          {/* Today's Invoiced Loans */}
          <Grid item xs={12} md={4}>
            <Box sx={{ p: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <Avatar sx={{ bgcolor: "#fef3c7", color: "#d97706", width: 32, height: 32 }}>
                  <AssignmentReturnIcon sx={{ fontSize: 18 }} />
                </Avatar>
                <Typography variant="subtitle2" fontWeight={600} color="#1e293b">
                  {t("Loans Invoiced")}
                </Typography>
              </Stack>

              <Typography variant="h3" fontWeight={700} color="#d97706">
                {todaySummary.loans.invoicedCount}
              </Typography>
              <Typography variant="caption" color="#64748b">
                {t("Loans invoiced today")}
              </Typography>

              {todaySummary.loans.invoicedCount > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="#64748b" sx={{ mb: 1, display: "block" }}>
                    {t("Recent")}:
                  </Typography>
                  <Stack spacing={0.5}>
                    {todaySummary.loans.list.slice(0, 3).map((loan, idx) => (
                      <Chip
                        key={idx}
                        label={`${loan.product_name || loan.name || t("Loan")} - ${loan.customer_name || ""}`}
                        size="small"
                        sx={{ height: 22, fontSize: "0.65rem", justifyContent: "flex-start" }}
                      />
                    ))}
                    {todaySummary.loans.list.length > 3 && (
                      <Typography variant="caption" color="#64748b">
                        +{todaySummary.loans.list.length - 3} {t("more")}
                      </Typography>
                    )}
                  </Stack>
                </Box>
              )}

              {todaySummary.loans.invoicedCount === 0 && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: "#f8fafc", borderRadius: 1 }}>
                  <Typography variant="caption" color="#64748b" sx={{ fontStyle: "italic" }}>
                    {t("No loans invoiced today")}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Charts Grid */}
      <Grid container spacing={1.5}>
        {/* Sales Analytics */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <CollapsibleSection
              title={t("Sales Analytics")}
              icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
              expanded={expandedPanels.sales}
              onToggle={() => togglePanel("sales")}
            >
              <Tabs
                value={salesTab}
                onChange={(e, v) => setSalesTab(v)}
                sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", minHeight: 36, py: 0.5 } }}
              >
                <Tab label={t("Revenue Trend")} />
                <Tab label={t("Top Customers")} />
              </Tabs>

              {salesTab === 0 && (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={salesAnalytics.revenueByMonth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                    <RechartsTooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }}
                      formatter={(value) => [formatCurrency(value), t("Revenue")]}
                    />
                    <Area type="monotone" dataKey="value" stroke="#3b82f6" fill="#dbeafe" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}

              {salesTab === 1 && (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={salesAnalytics.topCustomers} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                    <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 10 }} />
                    <RechartsTooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }}
                      formatter={(value) => [formatCurrency(value), t("Revenue")]}
                    />
                    <Bar dataKey="total" fill="#10b981" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CollapsibleSection>
          </Paper>
        </Grid>

        {/* Expenses Analytics */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <CollapsibleSection
              title={t("Expenses Analytics")}
              icon={<ReceiptIcon sx={{ fontSize: 18 }} />}
              expanded={expandedPanels.expenses}
              onToggle={() => togglePanel("expenses")}
            >
              <Tabs
                value={expensesTab}
                onChange={(e, v) => setExpensesTab(v)}
                sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", minHeight: 36, py: 0.5 } }}
              >
                <Tab label={t("By Category")} />
                <Tab label={t("Spending Trend")} />
              </Tabs>

              {expensesTab === 0 && (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={expensesAnalytics.byCategory}
                      dataKey="total"
                      nameKey="code"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={{ stroke: "#94a3b8" }}
                    >
                      {expensesAnalytics.byCategory.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }}
                      formatter={(value) => formatCurrency(value)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}

              {expensesTab === 1 && (
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={expensesAnalytics.monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`} />
                    <RechartsTooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }}
                      formatter={(value) => [formatCurrency(value), t("Expenses")]}
                    />
                    <Area type="monotone" dataKey="value" stroke="#ef4444" fill="#fef2f2" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CollapsibleSection>
          </Paper>
        </Grid>

        {/* Production Analytics */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <CollapsibleSection
              title={t("Production Analytics")}
              icon={<InventoryIcon sx={{ fontSize: 18 }} />}
              expanded={expandedPanels.production}
              onToggle={() => togglePanel("production")}
              badge={`${productionAnalytics.totalUnits} ${t("units")}`}
              badgeColor="#3b82f6"
            >
              <Tabs
                value={productionTab}
                onChange={(e, v) => setProductionTab(v)}
                sx={{ mb: 2, "& .MuiTab-root": { textTransform: "none", minHeight: 36, py: 0.5 } }}
              >
                <Tab label={t("Top Products")} />
                <Tab label={t("Distribution")} />
              </Tabs>

              {productionTab === 0 && (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={productionAnalytics.topProducts}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" tick={{ fontSize: 10, angle: -45, textAnchor: "end" }} height={80} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RechartsTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }} />
                    <Bar dataKey="quantity" fill="#8b5cf6" radius={[4, 4, 0, 0]} name={t("Quantity")} />
                  </BarChart>
                </ResponsiveContainer>
              )}

              {productionTab === 1 && (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={productionAnalytics.topProducts.slice(0, 6)}
                      dataKey="quantity"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      labelLine={{ stroke: "#94a3b8" }}
                    >
                      {productionAnalytics.topProducts.slice(0, 6).map((entry, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </CollapsibleSection>
          </Paper>
        </Grid>

        {/* Delivery Analytics */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            <CollapsibleSection
              title={t("Delivery Analytics")}
              icon={<LocalShippingIcon sx={{ fontSize: 18 }} />}
              expanded={expandedPanels.delivery}
              onToggle={() => togglePanel("delivery")}
              badge={deliveryAnalytics.overdueCount > 0 ? `${deliveryAnalytics.overdueCount} ${t("overdue")}` : null}
              badgeColor="#ef4444"
            >
              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={deliveryAnalytics.urgencyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RechartsTooltip contentStyle={{ borderRadius: 8, border: "1px solid #e5e7eb" }} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} name={t("Orders")}>
                        {deliveryAnalytics.urgencyData.map((entry, index) => (
                          <Cell key={index} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Stack spacing={1.5}>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 2, borderColor: "#e2e8f0", textAlign: "center" }}
                    >
                      <Typography variant="h4" fontWeight={700} color="#3b82f6">
                        {deliveryAnalytics.activeOrdersCount}
                      </Typography>
                      <Typography variant="caption" color="#64748b">
                        {t("Active Orders")}
                      </Typography>
                    </Paper>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 2, borderColor: "#e2e8f0", textAlign: "center" }}
                    >
                      <Typography
                        variant="h4"
                        fontWeight={700}
                        color={kpiStats.deliveryRate >= 90 ? "#10b981" : "#f59e0b"}
                      >
                        {kpiStats.deliveryRate.toFixed(0)}%
                      </Typography>
                      <Typography variant="caption" color="#64748b">
                        {t("On-Time Rate")}
                      </Typography>
                    </Paper>
                    <Paper
                      variant="outlined"
                      sx={{ p: 1.5, borderRadius: 2, borderColor: "#e2e8f0", textAlign: "center" }}
                    >
                      <Typography variant="h4" fontWeight={700} color="#8b5cf6">
                        {deliveryAnalytics.avgDeliveryTime.toFixed(1)}
                      </Typography>
                      <Typography variant="caption" color="#64748b">
                        {t("Avg Days to Ship")}
                      </Typography>
                    </Paper>
                  </Stack>
                </Grid>
              </Grid>
            </CollapsibleSection>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

export default MuiAnalyticsDashboard
